import type AccordionItem from './AccordionItem.js'
import { isServer, LitElement } from 'lit'

import { customElement, property } from 'lit/decorators.js'
import { DirectionController } from '../common/controllers/DirectionController.js'
import { RovingFocusController } from '../common/controllers/RovingFocusController.js'
import { observe } from '../common/decorators/observe.js'
import { NordEvent } from '../common/events.js'
import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'

import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import style from './Accordion.css'

let accordionCount = 1

/**
 * Parse a string attribute (`value="a,b"`) into an array of item values, while
 * leaving an array assigned via the property untouched.
 */
function parseValue(value: string | string[] | null): string[] {
  if (Array.isArray(value)) {
    return value
  }

  if (value == null || value === '') {
    return []
  }

  return value
    .split(',')
    .map(part => part.trim())
    .filter(Boolean)
}

/**
 * Event dispatched when the set of open accordion items changes.
 */
export class AccordionChangeEvent extends NordEvent {
  declare value: string[]

  constructor(value: string[]) {
    super('change')
    Object.defineProperty(this, 'value', {
      value,
      enumerable: true,
      writable: false,
    })
  }
}

/**
 * Accordion is a set of collapsible sections, each with a header that toggles
 * the visibility of its content. It coordinates a group of
 * [Accordion Item](/components/accordion-item/) elements, keyboard focus
 * movement between them, and which items are open.
 *
 * @status draft
 * @category container
 * @lightDom true
 *
 * @slot - Default slot for [Accordion Item](/components/accordion-item/) elements.
 *
 * @fires {AccordionChangeEvent} change - Dispatched when an item is expanded or collapsed. The event includes a `value` array of the currently open item values.
 *
 * @cssprop --n-accordion-item-gap - Spacing between accordion items.
 *
 * @remarks
 * `value` is the source of truth as an array property. The `value` attribute is
 * only read as a comma-separated seed: it is adopted on connect and again
 * whenever the attribute is set (e.g. `el.setAttribute('value', 'a,b')`), but it
 * is **not** reflected back to the DOM as the open set changes via interaction.
 * Read `accordion.value` (the property) to get the current open set; do not rely
 * on the `value` attribute staying in sync after the user interacts.
 */
@customElement('nord-accordion')
export default class Accordion extends DraftComponentMixin(LitElement) {
  // Watch `value` so `setAttribute('value', …)` re-adopts the seed even after
  // the property has been mutated by interaction (the property itself is not
  // reflected, so the attribute would otherwise freeze).
  static get observedAttributes() {
    return [...super.observedAttributes, 'value']
  }

  private accordionId = `nord-accordion-${accordionCount++}`

  private direction = new DirectionController(this)

  // Roving arrow-key focus across the trigger buttons (skips disabled ones,
  // honours orientation / loop / RTL).
  private rovingFocus = new RovingFocusController(this, {
    items: () => [...this.querySelectorAll<HTMLElement>('nord-accordion-trigger')],
    orientation: () => this.orientation,
    loop: () => this.loopFocus,
    isRTL: () => this.direction.isRTL,
    isFocusable: trigger => !(trigger as HTMLElement & { disabled: boolean }).disabled,
  })

  // Observes light-DOM children so dynamically added / reordered items get
  // wired up (ids, ARIA, open-state seeding).
  private childObserver?: MutationObserver

  // Guards against re-entrant re-setup scheduling.
  private setupScheduled = false

  /**
   * Whether multiple items can be open at the same time.
   */
  @property({ reflect: true, type: Boolean }) multiple = false

  /**
   * Whether the component should ignore user interaction.
   */
  @property({ reflect: true, type: Boolean }) disabled = false

  /**
   * The visual orientation of the accordion. Controls whether roving focus uses
   * left/right or up/down arrow keys.
   */
  @property({ reflect: true }) orientation: 'vertical' | 'horizontal' = 'vertical'

  /**
   * Whether to loop keyboard focus back to the first item when the end of the
   * list is reached while using the arrow keys.
   */
  @property({ reflect: true, type: Boolean, attribute: 'loop-focus' }) loopFocus = true

  /**
   * The value(s) of the item(s) that are open. Assign an array via the property,
   * or provide an initial comma-separated `value="a,b"` attribute.
   */
  @property({ type: Array, attribute: false }) value: string[] = []

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)
    this.setAttribute('role', 'region')
    this.adoptValueAttribute()
    this.seedDefaultValue()
    this.setup()
    this.observeChildren()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.childObserver?.disconnect()
    this.childObserver = undefined
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    super.attributeChangedCallback(name, oldValue, newValue)

    // `value` is not a reactive property attribute (it's `attribute: false`), so
    // Lit won't react to it. Re-adopt the comma-separated seed when it changes
    // so `setAttribute('value', …)` keeps working after interaction.
    if (name === 'value' && oldValue !== newValue) {
      this.value = parseValue(newValue)
    }
  }

  /**
   * Seed the open set from a `value="a,b"` attribute the first time we connect.
   * The property is the source of truth thereafter.
   */
  private adoptValueAttribute() {
    if (isServer || this.value.length > 0) {
      return
    }

    const attr = this.getAttribute('value')
    if (attr) {
      this.value = parseValue(attr)
    }
  }

  /**
   * Watch for items being added, removed, or reordered so newly slotted items
   * get wired up (ids / ARIA / open-state). Re-runs `setup()` deferred to a
   * microtask to avoid measuring/reflecting mid-mutation (reflection race).
   */
  private observeChildren() {
    if (isServer || this.childObserver) {
      return
    }

    this.childObserver = new MutationObserver(() => this.scheduleSetup())
    this.childObserver.observe(this, { childList: true, subtree: true })
  }

  private scheduleSetup() {
    if (this.setupScheduled) {
      return
    }

    this.setupScheduled = true
    // Defer to the update cycle so DOM mutations settle before we measure and
    // reflect (avoids the MutationObserver reflection race).
    void this.updateComplete.then(() => {
      this.setupScheduled = false
      if (this.isConnected) {
        // Wiring only on re-setup; do not re-seed (seeding must not fire change
        // after the initial connect).
        this.setup()
      }
    })
  }

  // Render in the light DOM so the items are the element's own content and the
  // injected stylesheet applies (no shadow boundary, like Table).
  protected createRenderRoot() {
    return this
  }

  private get items() {
    return [...this.querySelectorAll<AccordionItem>('nord-accordion-item')]
  }

  /**
   * If no `value` was provided but items declare `open`, adopt those as the
   * initial open set (mirrors `defaultValue` from open items).
   */
  private seedDefaultValue() {
    if (isServer || this.value.length > 0) {
      return
    }

    const openValues = this.items.filter(item => item.open).map(item => item.value)
    if (openValues.length > 0) {
      this.value = this.multiple ? openValues : openValues.slice(0, 1)
    }
  }

  /**
   * Wire up ids and ARIA relationships, delegate trigger activation, and reflect
   * the open state of every item.
   */
  private setup() {
    if (isServer) {
      return
    }

    this.items.forEach((item) => {
      // Reuse a stable id once assigned so re-running setup (e.g. after an item
      // is inserted in the middle) never reassigns ids to items that already
      // have them, which would break existing aria-controls/aria-labelledby.
      const id = item.partsId ?? `${this.accordionId}-item-${this.itemIdCount++}`
      item.partsId = id
      item.setupParts(id)
    })

    this.syncState()
  }

  // Monotonic per-accordion counter for stable item ids (never reused).
  private itemIdCount = 1

  @observe('value', 'updated')
  protected handleValueChange(oldValue: string[] | undefined) {
    this.syncState()

    if (oldValue !== undefined) {
      this.dispatchEvent(new AccordionChangeEvent(this.value))
    }
  }

  @observe('disabled')
  protected handleDisabledChange() {
    this.items.forEach((item) => {
      item.parentDisabled = this.disabled
    })
  }

  /**
   * Reflect the current open set onto every item.
   */
  private syncState() {
    this.items.forEach((item) => {
      item.parentDisabled = this.disabled
      item.open = this.value.includes(item.value)
      // Push open state to the trigger/panel synchronously so ARIA is correct
      // immediately, rather than waiting for the item's own update cycle.
      item.propagate()
    })
  }

  /**
   * Open or close a single item, respecting the `multiple` setting.
   */
  private setItemOpen(itemValue: string, open: boolean) {
    let next: string[]

    if (open) {
      next = this.multiple ? [...this.value, itemValue] : [itemValue]
    }
    else {
      next = this.value.filter(v => v !== itemValue)
    }

    this.value = next
  }

  /**
   * Toggle an item when its trigger is activated. Called directly by the
   * [Accordion Trigger](/components/accordion-trigger/).
   * @internal
   */
  activateTrigger(trigger: HTMLElement) {
    const item = trigger.closest<AccordionItem>('nord-accordion-item')

    if (!item || item.disabled || this.disabled) {
      return
    }

    const isOpen = this.value.includes(item.value)

    if (!this.multiple) {
      this.value = isOpen ? [] : [item.value]
    }
    else {
      this.setItemOpen(item.value, !isOpen)
    }
  }

  /**
   * Move keyboard focus between triggers. Called by triggers on arrow/Home/End.
   * The focused trigger is the current item, so the shared roving controller
   * resolves it from the active element.
   */
  handleTriggerKeydown(event: KeyboardEvent) {
    this.rovingFocus.handleKeydown(event)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-accordion': Accordion
  }
}
